import { useMemo, useState } from "react";
import {
    BaseEntryResultType,
    FilterProps,
    FilterValues,
    SupportedKmsTypeFilter,
} from "@kaltura/mediaspace-shared-types";
import {
    extractDefaultFilterValues,
    removeEmptyDefaultFilterValues,
    translate,
} from "@kaltura/mediaspace-shared-utils";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { SearchResultsSkeleton } from "./search-results/SearchResultsSkeleton";
import { SearchResultsData } from "./search-results/SearchResultsData";
import { FilterMenuProps } from "../search-bar/filter-menu/FilterMenu";
import { ExploreSideBarLayout } from "../explore-side-bar/ExploreSideBarLayout";
import { SearchBar } from "../search-bar/SearchBar";
import { FilterBarButtonWithModal } from "../filter-bar/FilterBarButtonWithModal";
import { SearchResultsContainer } from "../search-results-container/SearchResultsContainer";

export interface SimpleMediaSearchProps {
    searchUrl: string;
    filters: FilterProps<SupportedKmsTypeFilter>[];
    filterMenuProps: FilterMenuProps;
    initialValues?: FilterValues;
    pageSize: number;
    mediaContext?: string;
}

/**
 * Simple media search with filters
 */
export const SimpleMediaSearch = ({
    searchUrl,
    filters,
    filterMenuProps,
    initialValues = {},
    pageSize,
    mediaContext = "globalsearch",
}: SimpleMediaSearchProps) => {
    const [exploreEntry, setExploreEntry] = useState<BaseEntryResultType>();

    // filter values as the user set them (+ defaults)
    const [filterValues, setFilterValues] = useState(() => {
        return { ...extractDefaultFilterValues(filters), ...initialValues };
    });
    const minFilterValues = useMemo(
        () => removeEmptyDefaultFilterValues(filters, filterValues),
        [filters, filterValues]
    );

    const handleFilterChange = (values: Partial<FilterValues>) => {
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
        setExploreEntry(undefined);
    };
    const handleKeywordSearchSubmit = (value: string) => handleFilterChange({ keyword: value });

    return (
        <ExploreSideBarLayout
            searchResult={exploreEntry}
            onClose={() => setExploreEntry(undefined)}
            filterValues={filterValues}
            showPlayer={true}
            mediaContext={mediaContext}
        >
            <Stack direction={"column"} gap={3}>
                <SearchBar
                    buttons={[
                        <FilterBarButtonWithModal
                            filters={filters}
                            values={filterValues}
                            onChange={handleFilterChange}
                        />,
                    ]}
                    keywordSearchProps={{
                        onSubmit: handleKeywordSearchSubmit,
                        value: filterValues["keyword"] as string,
                        placeholder: translate("Search"),
                        ariaLabel: translate("Search"),
                    }}
                    filterMenuProps={
                        filterMenuProps && {
                            ...filterMenuProps,
                            values: filterValues,
                            onChange: handleFilterChange,
                        }
                    }
                />

                <SearchResultsContainer
                    searchUrl={searchUrl}
                    isWideLayout={!exploreEntry}
                    filterValues={minFilterValues}
                    pageSize={pageSize}
                    onEntryShowMore={(item) => setExploreEntry(item)}
                    searchResultsDisplayComponent={SearchResultsData}
                    searchResultsSkeletonComponent={SearchResultsSkeleton}
                />
            </Stack>
        </ExploreSideBarLayout>
    );
};
